import React, { useState, useEffect } from 'react';
import { 
  Lightbulb,
  Zap,
  Clock,
  RefreshCw,
  Calendar,
  ArrowRight,
  CheckCircle,
  Send,
  X,
  Info,
  Target,
  BrainCircuit,
  Users,
  BarChart3,
  Heart,
  Settings,
  Sparkles,
  MessageSquare,
  CheckSquare,
  Plus,
  Briefcase,
  PlayCircle,
  Rocket,
  Star
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// SEO Component
const SEOHead = () => {
  const title = "Business Challenge Solver - Turn Problems into Solutions | Lusidea";
  const description = "Transform your business challenges into actionable solutions. Get 3 different approaches with clear goals and next steps. Free business problem-solving tool.";
  const keywords = "business problem solving, challenge solver, solution finder, business strategy, problem analysis, innovation methodology";
  
  useEffect(() => {
    // Set document title
    document.title = title;
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', description);
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }
    
    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    metaKeywords.setAttribute('content', keywords);
    if (!document.querySelector('meta[name="keywords"]')) {
      document.head.appendChild(metaKeywords);
    }
    
    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.setAttribute('content', title);
    if (!document.querySelector('meta[property="og:title"]')) {
      document.head.appendChild(ogTitle);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.setAttribute('content', description);
    if (!document.querySelector('meta[property="og:description"]')) {
      document.head.appendChild(ogDescription);
    }
    
    const ogType = document.querySelector('meta[property="og:type"]') || document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.setAttribute('content', 'website');
    if (!document.querySelector('meta[property="og:type"]')) {
      document.head.appendChild(ogType);
    }
    
    // Structured data for SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Business Challenge Solver",
      "description": description,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "provider": {
        "@type": "Organization",
        "name": "Lusidea",
        "url": "https://www.lusidea.com"
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
    
    // Cleanup function
    return () => {
      const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('"BusinessApplication"')) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);
  
  return null;
};

// Color palette
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  secondary: '#fdb52a',
  white: '#ffffff'
};

// Challenge categories
const CHALLENGE_CATEGORIES = {
  customer: {
    label: 'Customer Focus',
    icon: Heart,
    color: 'pink',
    bgClass: 'bg-pink-50',
    textClass: 'text-pink-700',
    borderClass: 'border-pink-200'
  },
  operational: {
    label: 'Operations',
    icon: Settings,
    color: 'blue',
    bgClass: 'bg-blue-50',
    textClass: 'text-blue-700',
    borderClass: 'border-blue-200'
  },
  innovation: {
    label: 'Innovation',
    icon: Lightbulb,
    color: 'purple',
    bgClass: 'bg-purple-50',
    textClass: 'text-purple-700',
    borderClass: 'border-purple-200'
  }
};

// Example problems for inspiration
const EXAMPLE_PROBLEMS = [
  "Our customers wait too long for support responses",
  "Too much time spent on repetitive manual tasks",
  "Competitors launch new features faster than us",
  "Struggling to attract customers in our target market",
  "Team resists adopting new tools and processes",
  "High employee turnover affecting productivity"
];

// Simple button component
const Button = ({ children, onClick, isLoading = false, fullWidth = false, variant = 'primary', icon, className = '', disabled = false, size = 'md' }) => {
  const baseStyle = "rounded-lg font-medium transition-all flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2";
  const sizes = {
    sm: "py-2 px-4 text-sm",
    md: "py-3 px-6",
    lg: "py-4 px-8 text-lg"
  };
  const variants = {
    primary: `bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    outline: `bg-white border border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-700 focus:ring-gray-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    success: `bg-green-600 hover:bg-green-700 text-white focus:ring-green-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
      aria-label={typeof children === 'string' ? children : 'Button'}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />
      ) : (
        icon && <span className="mr-2" aria-hidden="true">{icon}</span>
      )}
      {children}
    </button>
  );
};

// Toast notification
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 4000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div 
      className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border max-w-sm flex items-center gap-3 ${styles[type]}`}
      role="alert"
      aria-live="assertive"
    >
      <CheckCircle className="h-5 w-5" aria-hidden="true" />
      <p className="font-medium">{message}</p>
      <button onClick={onClose} className="ml-auto hover:opacity-70" aria-label="Close notification">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Solution Card Component
const SolutionCard = ({ challenge, index }) => {
  const category = CHALLENGE_CATEGORIES[challenge.category] || CHALLENGE_CATEGORIES.customer;
  const CategoryIcon = category.icon;
  
  const colors = [
    { bg: 'bg-gradient-to-br from-blue-50 to-indigo-100', border: 'border-blue-200', accent: 'bg-blue-500', text: 'text-blue-700' },
    { bg: 'bg-gradient-to-br from-purple-50 to-pink-100', border: 'border-purple-200', accent: 'bg-purple-500', text: 'text-purple-700' },
    { bg: 'bg-gradient-to-br from-green-50 to-emerald-100', border: 'border-green-200', accent: 'bg-green-500', text: 'text-green-700' }
  ];
  
  const cardColor = colors[index % colors.length];
  
  return (
    <article className={`bg-white rounded-xl sm:rounded-2xl border-2 ${cardColor.border} shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-1`}>
      {/* Header */}
      <header className={`${cardColor.bg} px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-gray-100`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${cardColor.accent} rounded-lg sm:rounded-xl flex items-center justify-center shadow-md`}>
              <CategoryIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Approach #{index + 1}</h3>
              <span className={`text-xs sm:text-sm font-medium ${cardColor.text}`}>{category.label} Strategy</span>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-sm px-3 py-1 rounded-full self-start sm:self-auto">
            <span className="text-xs sm:text-sm font-semibold text-gray-700">Ready to Use</span>
          </div>
        </div>
      </header>
      
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* How Might We Question */}
        <section>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <MessageSquare className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-blue-600" />
            </div>
            <span className="text-xs sm:text-sm font-semibold text-blue-900 uppercase tracking-wide">Key Question</span>
          </div>
          <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
            {challenge.hmw_question}
          </h4>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            {challenge.hmw_rationale}
          </p>
        </section>
        
        {/* Goal Section */}
        <section className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-100">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center">
              <Target className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-green-600" />
            </div>
            <span className="text-xs sm:text-sm font-semibold text-green-900 uppercase tracking-wide">Goal</span>
          </div>
          <h5 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">{challenge.smart_goal.title}</h5>
          <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">{challenge.smart_goal.description}</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Success Metric</span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-gray-800">{challenge.smart_goal.success_metric}</p>
            </div>
            <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Timeline</span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-gray-800">{challenge.smart_goal.timeline}</p>
            </div>
          </div>
        </section>
        
        {/* Action Steps */}
        <section>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-100 rounded-full flex items-center justify-center">
              <PlayCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-orange-600" />
            </div>
            <span className="text-xs sm:text-sm font-semibold text-orange-900 uppercase tracking-wide">Next Steps</span>
          </div>
          <div className="space-y-3">
            {challenge.smart_goal.action_steps.slice(0, 3).map((step, idx) => (
              <div key={idx} className="flex items-start gap-3 sm:gap-4 p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100 hover:shadow-sm transition-all">
                <div className={`w-5 h-5 sm:w-6 sm:h-6 ${cardColor.accent} rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs sm:text-sm font-bold`}>
                  {idx + 1}
                </div>
                <p className="text-gray-800 font-medium text-sm sm:text-base leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </article>
  );
};

// Loading animation
const LoadingAnimation = () => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    { icon: BrainCircuit, text: "Analyzing your challenge...", color: "text-blue-600" },
    { icon: Lightbulb, text: "Finding solution approaches...", color: "text-purple-600" },
    { icon: Target, text: "Creating actionable goals...", color: "text-green-600" },
    { icon: Sparkles, text: "Finalizing your solutions...", color: "text-red-600" }
  ];
  
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 2, 100));
    }, 80);
    
    const stepInterval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length);
    }, 1500);
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
    };
  }, []);
  
  const CurrentIcon = steps[currentStep].icon;
  
  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 lg:p-12 max-w-lg mx-auto text-center">
      <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 sm:mb-8 relative">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle
            className="text-gray-100"
            strokeWidth="6"
            stroke="currentColor"
            fill="transparent"
            r="42"
            cx="50"
            cy="50"
          />
          <circle
            className="text-red-500 transition-all duration-500 ease-out"
            strokeWidth="6"
            strokeDasharray={264}
            strokeDashoffset={264 - (progress / 100) * 264}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r="42"
            cx="50"
            cy="50"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <CurrentIcon className={`h-6 w-6 sm:h-8 sm:w-8 ${steps[currentStep].color} animate-pulse`} />
        </div>
      </div>
      
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Creating Your Solutions</h3>
      <div className="flex items-center justify-center gap-3 mb-4 sm:mb-6">
        <div className={`h-2 w-2 rounded-full transition-all duration-300 ${steps[currentStep].color.replace('text-', 'bg-')}`} />
        <span className={`font-semibold text-sm sm:text-lg ${steps[currentStep].color}`}>
          {steps[currentStep].text}
        </span>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-red-500 to-purple-500 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
        Analyzing your challenge from multiple angles to create effective solution approaches.
      </p>
    </div>
  );
};

// Success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Toolkit Sent!</h3>
      <p className="text-gray-600 mb-4">
        Your solution toolkit has been sent to <strong>{email}</strong>
      </p>
      <p className="text-gray-600 text-sm">
        Our team will follow up to help you implement these solutions.
      </p>
    </div>
  );
};

// Main component
const InnovationChallengeReframer = () => {
  const [originalProblem, setOriginalProblem] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reframedChallenges, setReframedChallenges] = useState([]);
  const [toast, setToast] = useState(null);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  const generateSolutions = async () => {
    if (!originalProblem.trim()) {
      setToast({ message: 'Please describe your challenge first!', type: 'error' });
      return;
    }
    
    setIsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-3-7-sonnet-20250219',
          max_tokens: 3500,
          temperature: 0.8,
          messages: [{
            role: 'user',
            content: `Transform this business problem into 3 different solution approaches, each with a "How Might We" question and corresponding SMART goal:

Original Problem: "${originalProblem}"

For each approach, provide:
- Category: customer, operational, or innovation
- HMW_question: A creative "How might we..." question that reframes the problem positively
- HMW_rationale: 1 sentence explaining why this reframe is valuable
- Smart_goal object with:
  - Title: Specific goal name
  - Description: Clear 2-sentence goal description
  - Success_metric: Exact metric to measure success
  - Timeline: Realistic timeframe with milestones
  - Action_steps: Array of 4 specific first steps

Make each approach focus on a different angle. Use positive, solution-focused language.

Respond in this JSON format:
{
  "challenges": [
    {
      "category": "customer|operational|innovation",
      "hmw_question": "How might we...",
      "hmw_rationale": "This reframe helps because...",
      "smart_goal": {
        "title": "Specific Goal Name",
        "description": "Clear description of what this goal achieves and why it matters.",
        "success_metric": "Specific measurable outcome",
        "timeline": "Realistic timeframe with milestones",
        "action_steps": ["Step 1", "Step 2", "Step 3", "Step 4"]
      }
    }
  ]
}`
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to generate solutions');
      
      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const solutionsData = JSON.parse(jsonMatch[0]);
        setTimeout(() => {
          setReframedChallenges(solutionsData.challenges || []);
          setIsLoading(false);
        }, 2000);
      } else {
        throw new Error('Could not parse response');
      }
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      setToast({ message: 'Something went wrong. Please try again.', type: 'error' });
    }
  };
  
  const handleEmailSubmit = (email) => {
    setIsEmailSubmitting(true);
    setTimeout(() => {
      setIsEmailSubmitting(false);
      setEmailSubmitted(true);
      setSubmittedEmail(email);
      setToast({ message: 'Solution toolkit sent to your email!', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };
  
  const handleReset = () => {
    setOriginalProblem('');
    setReframedChallenges([]);
    setEmailSubmitted(false);
    setSubmittedEmail('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSelectExample = (text) => {
    setOriginalProblem(text);
    const textarea = document.getElementById('problem-input');
    if (textarea) {
      textarea.scrollIntoView({ behavior: 'smooth' });
      textarea.focus();
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead />
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header */}
      <header className="bg-white pt-20 sm:pt-24 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-red-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
            <BrainCircuit className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Business Challenge Solver
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Turn any business problem into 3 clear solution approaches with specific goals and action steps
          </p>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        
        {/* Input Section */}
        {!isLoading && reframedChallenges.length === 0 && !emailSubmitted && (
          <div className="space-y-8">
            
            {/* Main Input */}
            <section className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 lg:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                What challenge is your business facing?
              </h2>
              <div className="mb-6 sm:mb-8">
                <label htmlFor="problem-input" className="sr-only">Describe your business challenge</label>
                <textarea
                  id="problem-input"
                  value={originalProblem}
                  onChange={(e) => setOriginalProblem(e.target.value)}
                  placeholder="Describe the problem or challenge your business is facing... The more specific you are, the better solutions we can create for you."
                  className="w-full p-4 sm:p-5 border border-gray-300 rounded-lg sm:rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none min-h-32 sm:min-h-40 text-gray-800 text-base sm:text-lg leading-relaxed resize-y"
                  rows={6}
                />
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-3 gap-2">
                  <span className="text-sm text-gray-500">Be specific about what's not working and why it matters</span>
                  <span className="text-sm text-gray-400">{originalProblem.length} characters</span>
                </div>
              </div>
              <div className="text-center">
                <Button
                  onClick={generateSolutions}
                  disabled={!originalProblem.trim()}
                  icon={<Sparkles className="h-5 w-5" />}
                  size="lg"
                  className="px-8 sm:px-12 py-3 sm:py-4 text-lg sm:text-xl"
                >
                  Create Solutions
                </Button>
              </div>
            </section>
            
            {/* Example Challenges */}
            <section>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 text-center">
                Or try one of these example challenges:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EXAMPLE_PROBLEMS.map((problem, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectExample(problem)}
                    className="p-4 sm:p-5 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl text-left hover:border-red-300 hover:shadow-lg transition-all group hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    aria-label={`Use example: ${problem}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                        <Lightbulb className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-500 group-hover:text-red-600 transition-colors">
                        Try this example →
                      </span>
                    </div>
                    <p className="text-gray-800 font-semibold text-sm sm:text-base leading-relaxed">
                      {problem}
                    </p>
                  </button>
                ))}
              </div>
            </section>
            
            {/* How it works */}
            <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-blue-200">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                  <Info className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-blue-900 mb-3 sm:mb-4">How It Works</h3>
                  <p className="text-blue-800 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
                    We transform your problem into 3 different solution approaches. Each approach includes a creative "How Might We" 
                    question to reframe your thinking, plus a specific goal with clear next steps you can take immediately.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="bg-white/70 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-lg flex items-center justify-center mb-2">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Analyze Problem</h4>
                      <p className="text-xs sm:text-sm text-gray-700">Examine your challenge from multiple angles</p>
                    </div>
                    <div className="bg-white/70 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-500 rounded-lg flex items-center justify-center mb-2">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Generate Solutions</h4>
                      <p className="text-xs sm:text-sm text-gray-700">Create 3 distinct approaches with key questions</p>
                    </div>
                    <div className="bg-white/70 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-lg flex items-center justify-center mb-2">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Action Steps</h4>
                      <p className="text-xs sm:text-sm text-gray-700">Provide clear goals with immediate next steps</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
        
        {/* Loading */}
        {isLoading && <LoadingAnimation />}
        
        {/* Results */}
        {!isLoading && reframedChallenges.length > 0 && !emailSubmitted && (
          <div>
            {/* Results Header */}
            <section className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 mb-8 sm:mb-12">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                    <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Your Solution Approaches</h2>
                    <p className="text-gray-600 text-sm sm:text-lg">3 different ways to tackle: "{originalProblem.slice(0, 80)}..."</p>
                  </div>
                </div>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  icon={<RefreshCw className="h-4 w-4" />}
                  size="lg"
                  className="self-start sm:self-auto"
                >
                  Try New Challenge
                </Button>
              </div>
            </section>
            
            {/* Solution Cards */}
            <div className="space-y-6 sm:space-y-8 mb-8 sm:mb-12">
              {reframedChallenges.map((challenge, idx) => (
                <SolutionCard key={idx} challenge={challenge} index={idx} />
              ))}
            </div>
            
            {/* Bottom Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              
              {/* Email Capture */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl sm:rounded-2xl border-2 border-red-200 p-6 sm:p-8">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-500 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Briefcase className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Get Your Solution Toolkit</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Complete worksheets and guides to implement these solutions with your team</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="sr-only">Your name</label>
                    <input
                      type="text"
                      id="name"
                      placeholder="Your name"
                      className="w-full p-3 sm:p-4 border border-gray-300 rounded-lg sm:rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none text-base sm:text-lg"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="sr-only">Your email address</label>
                    <input
                      type="email"
                      id="email"
                      placeholder="your@email.com"
                      className="w-full p-3 sm:p-4 border border-gray-300 rounded-lg sm:rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none text-base sm:text-lg"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      const email = document.getElementById('email').value;
                      if (email) handleEmailSubmit(email);
                    }}
                    icon={<Send className="h-5 w-5" />}
                    fullWidth
                    size="lg"
                    isLoading={isEmailSubmitting}
                  >
                    Send Solution Toolkit
                  </Button>
                </div>
                
                <p className="text-xs sm:text-sm text-gray-500 text-center mt-4">
                  Includes team worksheets and step-by-step implementation guides
                </p>
              </div>
              
              {/* Implementation Support */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl border-2 border-blue-200 p-6 sm:p-8">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-500 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Users className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Need Implementation Help?</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Book a strategy session to discuss which approach fits best and create an action plan</p>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-white/70 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 border border-blue-200">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="font-semibold text-gray-800 text-sm sm:text-base">Free 30-minute consultation</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="font-semibold text-gray-800 text-sm sm:text-base">Prioritize solutions for your context</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="font-semibold text-gray-800 text-sm sm:text-base">Create 90-day implementation roadmap</span>
                      </div>
                    </div>
                  </div>
                  
                  <a
                    href="https://calendly.com/lusidea/implementation-support"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button
                      icon={<Calendar className="h-5 w-5" />}
                      fullWidth
                      size="lg"
                      variant="outline"
                    >
                      Book Strategy Session
                    </Button>
                  </a>
                </div>
              </div>
            </div>
            
            {/* Next Steps Call-to-Action */}
            <section className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center text-white mt-8 sm:mt-12">
              <h3 className="text-xl sm:text-2xl font-bold mb-3">Ready to Transform Your Challenge?</h3>
              <p className="text-gray-300 text-sm sm:text-lg mb-6 max-w-2xl mx-auto leading-relaxed">
                Choose the approach that resonates most with your team and start with the outlined next steps. 
                Each solution is designed to deliver measurable results within 90 days.
              </p>
              <div className="flex flex-col sm:flex-row sm:justify-center gap-4">
                <Button
                  onClick={handleReset}
                  variant="outline"
                  icon={<Plus className="h-5 w-5" />}
                  size="lg"
                  className="border-white text-black hover:bg-white hover:text-gray-900"
                >
                  Solve Another Challenge
                </Button>
                <a
                  href="https://calendly.com/lusidea/team-workshop"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    icon={<Rocket className="h-5 w-5" />}
                    size="lg"
                    fullWidth
                    className="sm:w-auto"
                  >
                    Run Team Workshop
                  </Button>
                </a>
              </div>
            </section>
          </div>
        )}
        
        {/* Success */}
        {emailSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
    </div>
  );
};

export default InnovationChallengeReframer;